import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Spinner,
  Table,
} from "react-bootstrap";
import PageHeader from "../shared/PageHeader";
import { Order } from "../../types";
import { SupplierInfo } from "../../types/supplier";
import { getOrders } from "../../services/OrderService";
import { getSuppliersDropdown } from "../../services/SupplierService";

const toIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const todayIso = (): string => toIsoDate(new Date());

const daysAgoIso = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toIsoDate(d);
};

const sevenDaysAgoIso = (): string => daysAgoIso(6);

type PresetKey = "1d" | "7d" | "30d" | "custom";

const PRESETS: { key: PresetKey; label: string; days: number }[] = [
  { key: "1d", label: "Today", days: 0 },
  { key: "7d", label: "Last 7 Days", days: 6 },
  { key: "30d", label: "Last 30 Days", days: 29 },
];

const formatGBP = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "";
  return `£ ${value.toFixed(2)}`;
};

const humanizeType = (type?: string): string => {
  if (!type) return "";
  return type
    .split("-")
    .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s))
    .join(" ");
};

const toNumberOrNull = (v: unknown): number | null => {
  if (v === undefined || v === null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isNaN(n) ? null : n;
};

interface SummaryRow {
  key: string;
  productName: string;
  size: string;
  dailyQty: Record<string, number>;
  totalQty: number;
  unitPrice: number | null;
  totalAmount: number;
}

const Summary: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [startDate, setStartDate] = useState<string>(sevenDaysAgoIso());
  const [endDate, setEndDate] = useState<string>(todayIso());
  const [activePreset, setActivePreset] = useState<PresetKey>("7d");
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);

  const applyPreset = (preset: typeof PRESETS[number]) => {
    setStartDate(daysAgoIso(preset.days));
    setEndDate(todayIso());
    setActivePreset(preset.key);
  };

  const fetchData = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    try {
      const [orderResult, supplierResult] = await Promise.all([
        getOrders(),
        getSuppliersDropdown(),
      ]);
      const orderList = Array.isArray(orderResult) ? orderResult : [];
      const supplierList: SupplierInfo[] = Array.isArray(supplierResult) ? supplierResult : [];
      setOrders(orderList);
      setSuppliers(supplierList);
    } catch (error) {
      console.error("Failed to fetch summary data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayKeys = useMemo<string[]>(() => {
    if (!startDate || !endDate) return [];
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    if (end < start) return [];
    const list: string[] = [];
    const cursor = new Date(start);
    for (let i = 0; i < 90 && cursor <= end; i++) {
      list.push(toIsoDate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return list;
  }, [startDate, endDate]);

  const formatDayHeader = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, "0");
    const mon = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${mon}`;
  };

  const rows = useMemo<SummaryRow[]>(() => {
    const map = new Map<string, SummaryRow>();
    const dayKeySet = new Set(dayKeys);

    const upsert = (
      name: string,
      code: string,
      size: string,
      dayKey: string,
      qty: number,
      unitPrice: number | null,
    ) => {
      const key = `${name}|${code}|${size}`;
      let row = map.get(key);
      if (!row) {
        row = {
          key,
          productName: name,
          size,
          dailyQty: {},
          totalQty: 0,
          unitPrice: null,
          totalAmount: 0,
        };
        map.set(key, row);
      }
      row.dailyQty[dayKey] = (row.dailyQty[dayKey] || 0) + qty;
      row.totalQty += qty;
      if (unitPrice != null) row.unitPrice = unitPrice;
    };

    orders.forEach((order) => {
      if (selectedSupplierId != null && order.supplierId !== selectedSupplierId) return;
      if (!order.dateOrdered) return;
      const ordered = new Date(order.dateOrdered);
      if (Number.isNaN(ordered.getTime())) return;
      const dayKey = toIsoDate(ordered);
      if (!dayKeySet.has(dayKey)) return;

      let parsed: any = null;
      if (order.formData) {
        try {
          parsed = JSON.parse(order.formData);
        } catch {
          parsed = null;
        }
      }
      const fd = parsed || {};

      if (Array.isArray(fd.products) && fd.products.length > 0) {
        fd.products.forEach((p: any) => {
          const pPrice = toNumberOrNull(p?.price);
          const pQuantity = toNumberOrNull(p?.quantity);

          let qty: number;
          let unitPrice: number | null;
          if (pPrice != null) {
            unitPrice = pPrice;
            qty = pQuantity ?? 1;
          } else if (pQuantity != null) {
            unitPrice = pQuantity;
            qty = 1;
          } else {
            unitPrice = null;
            qty = 0;
          }

          const name = typeof p?.name === "string" ? p.name : "";
          const code = typeof p?.code === "string" ? p.code : "";
          const size = typeof p?.size === "string" ? p.size : "";
          if (!name && !code) return;
          upsert(name, code, size, dayKey, qty, unitPrice);
        });
      } else {
        const fdPrice = toNumberOrNull(fd.price);
        const fdQuantity = toNumberOrNull(fd.quantity);

        let qty: number;
        let unitPrice: number | null;
        if (fdPrice != null) {
          unitPrice = fdPrice;
          qty = fdQuantity ?? 1;
        } else if (fdQuantity != null) {
          unitPrice = fdQuantity;
          qty = 1;
        } else {
          unitPrice = null;
          qty = 0;
        }

        const name = humanizeType(order.type);
        const code = (typeof fd.code === "string" && fd.code) || "";
        const size = (typeof fd.size === "string" && fd.size) || "";
        if (!name && !code) return;
        upsert(name, code, size, dayKey, qty, unitPrice);
      }
    });

    const result = Array.from(map.values());
    result.forEach((r) => {
      r.totalAmount = r.unitPrice != null ? r.totalQty * r.unitPrice : 0;
    });
    result.sort((a, b) => a.productName.localeCompare(b.productName));
    return result;
  }, [orders, dayKeys, selectedSupplierId]);

  const grandTotal = useMemo(() => {
    let qty = 0;
    let amount = 0;
    rows.forEach((r) => {
      qty += r.totalQty;
      amount += r.totalAmount;
    });
    return { qty, amount };
  }, [rows]);

  const formatQty = (n: number): string => {
    if (!n) return "";
    return n % 1 === 0 ? String(n) : n.toFixed(2);
  };

  return (
    <div className="container-fluid min-vh-100 bg-body-tertiary px-3 px-lg-4 py-4">
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <PageHeader title="Summary" subTitle="Sales by Product" />

          <div className="ms-lg-auto">
            <Button
              variant="light"
              onClick={() => fetchData()}
              disabled={refreshing || loading}
              className="border rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
            >
              {refreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-lg-4">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {PRESETS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={activePreset === p.key ? "dark" : "outline-secondary"}
                  className="rounded-pill px-3"
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </Button>
              ))}
              <Button
                size="sm"
                variant={activePreset === "custom" ? "dark" : "outline-secondary"}
                className="rounded-pill px-3"
                onClick={() => setActivePreset("custom")}
              >
                Custom Range
              </Button>
            </div>

            <Row className="g-3 mb-3 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="small text-secondary mb-1">
                  Supplier
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-truck"></i>
                  </InputGroup.Text>
                  <Form.Select
                    value={selectedSupplierId ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedSupplierId(v === "" ? null : parseInt(v, 10));
                    }}
                    className="border-start-0"
                    aria-label="Supplier"
                  >
                    <option value="">All Suppliers</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.businessDisplayName}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col xs={6} md={4}>
                <Form.Label className="small text-secondary mb-1">
                  Start Date
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar3"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    value={startDate}
                    max={endDate || undefined}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setActivePreset("custom");
                    }}
                    className="border-start-0"
                    aria-label="Start date"
                  />
                </InputGroup>
              </Col>
              <Col xs={6} md={4}>
                <Form.Label className="small text-secondary mb-1">
                  End Date
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar3"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="date"
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setActivePreset("custom");
                    }}
                    className="border-start-0"
                    aria-label="End date"
                  />
                </InputGroup>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="small text-uppercase text-secondary">
                  <tr>
                    <th className="border-bottom pb-3 pt-2" style={{ position: "sticky", left: 0, background: "#fff", zIndex: 2 }}>
                      Product
                    </th>
                    <th className="border-bottom pb-3 pt-2">Size</th>
                    {dayKeys.map((d) => (
                      <th
                        key={d}
                        className="border-bottom pb-3 pt-2 text-end"
                        style={{ minWidth: 64 }}
                      >
                        {formatDayHeader(d)}
                      </th>
                    ))}
                    <th className="border-bottom pb-3 pt-2 text-end">Total Qty</th>
                    <th className="border-bottom pb-3 pt-2 text-end">Unit Price</th>
                    <th className="border-bottom pb-3 pt-2 text-end">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={dayKeys.length + 5} className="text-center text-muted py-5">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading summary...
                      </td>
                    </tr>
                  ) : dayKeys.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-5">
                        Select a valid date range.
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={dayKeys.length + 5} className="text-center text-muted py-5">
                        No orders found for the selected date range.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.key}>
                        <td className="py-3 fw-semibold text-dark" style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                          {r.productName}
                        </td>
                        <td className="py-3 text-body-secondary">{r.size}</td>
                        {dayKeys.map((d) => (
                          <td key={d} className="py-3 text-end text-body-secondary">
                            {formatQty(r.dailyQty[d] || 0)}
                          </td>
                        ))}
                        <td className="py-3 text-end fw-semibold">
                          {formatQty(r.totalQty)}
                        </td>
                        <td className="py-3 text-end">
                          {r.unitPrice != null ? formatGBP(r.unitPrice) : ""}
                        </td>
                        <td className="py-3 text-end">
                          {r.unitPrice != null ? formatGBP(r.totalAmount) : ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {!loading && rows.length > 0 && (
                  <tfoot>
                    <tr className="border-top fw-semibold">
                      <td className="py-3" style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                        Total
                      </td>
                      <td className="py-3"></td>
                      {dayKeys.map((d) => (
                        <td key={d} className="py-3"></td>
                      ))}
                      <td className="py-3 text-end">{formatQty(grandTotal.qty)}</td>
                      <td className="py-3 text-end"></td>
                      <td className="py-3 text-end">{formatGBP(grandTotal.amount)}</td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Summary;
